<template>
<v-card>
  <v-card-title>
    <span class="headline">{{ action === 'add' ? 'New job' : `Edit job ${number}` }}</span>
  </v-card-title>
  <v-card-text>
    <v-container>
      <v-row dense>
        <v-col v-if="action === 'add'" cols="12" sm="3">
          <v-text-field v-model="number" label="Number" autocomplete="off"></v-text-field>
        </v-col>
        <v-col cols="12" sm="6">
          <v-text-field v-model="name" label="Name" autocomplete="off"></v-text-field>
        </v-col>
        <v-col cols="12" sm="6">
          <v-text-field v-model="client" label="Client" autocomplete="off"></v-text-field>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="6" sm="4">
          <v-select v-model="status" :items="statusOptions"
                    item-text="value" item-value="value" label="Status">
            <template v-slot:item="{ item, on, attrs }">
              <v-list-item v-bind="attrs" v-on="on">
                <v-chip small :color="item.colour" dark>{{ item.value }}</v-chip>
              </v-list-item>
            </template>
            <template v-slot:selection="{ item }">
              <v-chip small :color="item.colour" dark>{{ item.value }}</v-chip>
            </template>
          </v-select>
        </v-col>
        <v-col cols="6" sm="4">
          <user-select v-model="managerId" label="Manager"></user-select>
        </v-col>
        <v-col cols="6" sm="4">
          <v-select v-model="department" :items="departmentOptions"
                    label="Department" clearable></v-select>
        </v-col>
      </v-row>
      <v-row>

        <v-col cols="6" sm="4">
          <v-select v-model="monthMonth" :items="monthOptions"
                    label="Month" clearable></v-select>
        </v-col>
        <v-col cols="6" sm="4">
          <v-select v-model="monthYear" :items="yearOptions"
                    label="Year" clearable></v-select>
        </v-col>
        <v-col cols="12" sm="4">
          <v-menu v-model="colourPickerOpen" offset-y :close-on-content-click="false">
            <template v-slot:activator="{ on, attrs }">
              <v-text-field v-model="colour" label="Colour" readonly
                            v-bind="attrs" v-on="on">
                <template v-slot:prepend>
                  <v-icon :color="colour">mdi-circle</v-icon>
                </template>
              </v-text-field>
            </template>
            <v-color-picker v-model="colour" mode="hexa" hide-inputs></v-color-picker>
          </v-menu>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="12">
          <v-textarea v-model="description" label="Description"></v-textarea>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="6">
          <v-checkbox v-model="onSocials" label="On socials" hide-details></v-checkbox>
        </v-col>
        <v-col cols="6">
          <v-checkbox v-model="onImdb" label="On IMDb" hide-details></v-checkbox>
        </v-col>
      </v-row>
    </v-container>
  </v-card-text>
  <v-card-actions>
    <v-btn v-if="action === 'edit'" text :color="websiteProject ? 'indigo' : 'green'"
           @click="openWebsiteProject">
      {{ websiteProject ? 'Website Project' : 'Add Website Project' }}
    </v-btn>
    <v-spacer></v-spacer>
    <v-btn text @click="$emit('close')">Cancel</v-btn>
    <v-btn text color="primary" @click="save">Save</v-btn>
  </v-card-actions>
</v-card>
</template>

<script>
import * as api from '../../api'
import { jobStatuses } from '../../data'
import { randomColourHex } from '../../util'
import UserSelect from '../ui/UserSelect'

export default {
  name: 'EditJob',
  components: { UserSelect },
  data() {
    return {
      action: 'edit',
      jobId: null,
      number: '',
      name: '',
      client: '',
      status: null,
      managerId: null,
      department: null,
      monthMonth: null,
      monthYear: null,
      description: '',
      colour: '',
      colourPickerOpen: false,
      statusOptions: jobStatuses,
      departmentOptions: ['SFX', 'SP'],
      onSocials: false,
      onImdb: false
    }
  },
  computed: {
    month() {
      if (this.monthMonth && this.monthYear) return `${this.monthYear}-${this.monthMonth}`
      return null
    },
    monthOptions() {
      return [
        { text: 'Jan', value: '01' }, { text: 'Feb', value: '02' },
        { text: 'Mar', value: '03' }, { text: 'Apr', value: '04' },
        { text: 'May', value: '05' }, { text: 'Jun', value: '06' },
        { text: 'Jul', value: '07' }, { text: 'Aug', value: '08' },
        { text: 'Sep', value: '09' }, { text: 'Oct', value: '10' },
        { text: 'Nov', value: '11' }, { text: 'Dec', value: '12' }
      ]
    },
    yearOptions() {
      const current = new Date().getFullYear()
      return Array.from({ length: 10 }, (_, i) => String(current - 5 + i))
    },
    websiteProject() {
      return this.$store.state.websiteProjects.find(p => p.job === this.jobId) || null
    }
  },
  created() {
    this.$store.dispatch('getWebsiteProjects')
  },
  methods: {
    newJob() {
      this.action = 'add'
      this.jobId = null
      const jobs = this.$store.state.jobs
      const max = jobs.reduce((m, j) => Math.max(m, parseInt(j.number) || 0), 0)
      this.number = String(max + 1)
      this.name = ''
      this.client = ''
      this.status = null
      this.managerId = null
      this.department = null
      this.monthMonth = null
      this.monthYear = null
      this.description = ''
      this.colour = randomColourHex()
    },
    setJob(job) {
      this.action = 'edit'
      this.jobId = job.id
      this.number = job.number
      this.name = job.name || ''
      this.client = job.client || ''
      this.status = job.status || null
      this.managerId = job.manager ? job.manager.id : null
      this.department = job.department || null
      if (job.month) {
        const [y, m] = job.month.split('-')
        this.monthYear = y
        this.monthMonth = m
      } else {
        this.monthYear = null
        this.monthMonth = null
      }
      this.description = job.description || ''
      this.colour = job.colour || ''
      this.onSocials = job.on_socials || false
      this.onImdb = job.on_imdb || false
    },
    async openWebsiteProject() {
      let id
      if (this.websiteProject) {
        id = this.websiteProject.id
      } else {
        const result = await api.post('/website/project', {
          job_id: this.jobId,
          title: this.name,
          client: this.client,
          year: this.monthYear || new Date().getFullYear()
        })
        await this.$store.dispatch('getWebsiteProjects', true)
        id = result.id
      }
      this.$emit('close')
      this.$router.push({ name: 'website-projects', params: { id } })
    },
    async save() {
      const payload = {
        name: this.name,
        client: this.client,
        status: this.status,
        manager_id: this.managerId,
        department: this.department,
        month: this.month,
        description: this.description,
        colour: this.colour,
        on_socials: this.onSocials,
        on_imdb: this.onImdb
      }
      if (this.action === 'add') {
        await api.post('/job', { number: this.number, ...payload })
      } else {
        await api.put(`/job/${this.jobId}`, payload)
      }
      this.$emit('close')
      this.$store.dispatch('getJobs', true)
    }
  }
}
</script>

<style scoped lang="scss">
</style>
